import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

const createHelpers = (width, height, css) => {
    // somehow sizes is ending up in markup, even if it is not a valid svg attribute
    // until we have a better solution, just render it empty, instead to '[Object object]'
    const sanitizeSizes = sizes =>
        Object.defineProperty(sizes, 'toString', {
            value: () => '',
            enumerable: false,
        });

    const getDimensions = (size, sizes) => {
        if (size && typeof size.width === 'number' && typeof size.height === 'number') {
            return size;
        }

        return size && sizes[size] ? sizes[size] : { width, height };
    };

    const getCss = (size, sizes, fillColor, fillColorRule, noStyles) => {
        if (noStyles) {
            return '';
        }

        const dimensions = getDimensions(size, sizes);
        const fillRule = fillColor && fillColorRule ? `${fillColorRule}{ fill: ${fillColor}; }` : '';

        return css`
            width: ${dimensions.width}px;
            height: ${dimensions.height}px;
            ${fillRule}
        `;
    };

    const propsToCss = ({ size, sizes, fillColor, fillColorRule, noStyles }) =>
        getCss(size, sizes, fillColor, fillColorRule, noStyles);

    return {
        getCss,
        getDimensions,
        propsToCss,
        sanitizeSizes,
    };
};

const width = '1024';
const height = '1024';
const viewBox = '0 0 1024 1024';

const { getDimensions, getCss, propsToCss, sanitizeSizes } = createHelpers(width, height, css);

const sizes = sanitizeSizes({
    small: { width: 16, height: 16 },
    medium: { width: 18, height: 18 },
    large: { width: 24, height: 24 },
});

const Image = styled.svg`
    ${propsToCss}
`;

const children = (
    <Fragment>
        <path
            d="M825.6 428.8C795.733 281.6 667.733 170.667 512 170.667c-123.733 0-230.4 70.4-283.733 172.8C100.267 356.267 0 465.067 0 597.333c0 140.8 115.2 256 256 256h554.667C928 853.333 1024 757.333 1024 640c0-113.067-87.467-204.8-198.4-211.2zM810.667 768H256c-93.867 0-170.667-76.8-170.667-170.667 0-87.466 66.134-160 151.467-168.533l44.8-4.267L302.933 384c40.534-78.933 119.467-125.867 206.934-125.867 110.933 0 209.066 78.934 230.4 189.867l12.8 64 66.133 4.267c66.133 4.266 119.467 59.733 119.467 125.866 0 68.267-57.6 125.867-128 125.867zM341.333 554.667h108.8v128h123.734v-128h108.8L512 384 341.333 554.667z"
            key="key-0"
        />
    </Fragment>
);

const defaultProps = {
    children,
    viewBox,
    fillColor: null,
    fillColorRule: '&&& path, &&& use, &&& g',
    sizes,
    size: null,
};

const propTypes = {
    fillColor: PropTypes.string,
    fillColorRule: PropTypes.string,
    viewBox: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    size: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            height: PropTypes.number.isRequired,
            width: PropTypes.number.isRequired,
        }),
    ]),
    sizes: PropTypes.shape({
        height: PropTypes.number,
        width: PropTypes.number,
    }),
};

export default Object.assign(Image, {
    getDimensions,
    getCss,
    defaultProps,
    propTypes,
    displayName: 'UploadCloud',
});
